import { useCallback, useMemo } from 'react';
import { Alert, CardFooter, UncontrolledTooltip } from 'reactstrap';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { isEmpty } from 'lodash-es';
import moment from 'moment';
import { faBolt, faWandMagicSparkles } from '@fortawesome/free-solid-svg-icons';
import { useFormValues } from '@s20/components/forms/FormContext';
import { httpClient } from '@s20/services/httpClient';
import { pluralize } from '@s20/common/util/l10n';
import {
    FlexEndRow, FormCheckbox, StyledButton, toasty,
} from '../../common/forms/FormElements';
import { Modal } from '../../common/Modal';
import { ApiRoutes } from '../../Common';
import { getPlanningBoard } from '../../dispatch/Dispatch';
import { useJobQueryKey } from './common/useJobQueryKey.hook';

export const Footer = () => {
    const { selectedDate, dispatchGroupId, replacingAssignmentId } = useFormValues();
    const day = moment(selectedDate).toDate();

    const query = useJobQueryKey();
    const queryClient = useQueryClient();

    const { data: jobData } = useQuery(query);

    const planningBoard = useMemo(() => getPlanningBoard(jobData || [], [day]), [jobData, day]);

    // A replace collapses its slot client-side, which makes the board look like it has an
    // unfilled slot even though the assignment is still filled server-side — so auto-assign
    // can't act on it. Disable it until the replacement is chosen or cancelled.
    const isReplaceInProgress = !!replacingAssignmentId;

    const hasOpenSlots = planningBoard.staff.unfilled > 0 || planningBoard.equipment.unfilled > 0;

    // Recalculating replaces scheduled assignments, so the button has to stay live on a fully
    // assigned day — that's exactly the case where a new job has come in since the last run.
    // Counts only what the server will actually replace: auto-created, un-notified assignments.
    // A manually-assigned slot is scheduled but never recalculable, so counting plain `scheduled`
    // here would leave the button live for a day auto-assign cannot change.
    const hasRegenerableAssignments = planningBoard.staff.regenerable > 0 || planningBoard.equipment.regenerable > 0;

    const canAutoAssign = (hasOpenSlots || hasRegenerableAssignments) && !isReplaceInProgress;

    const handleSendNotifications = useMutation({
        mutationFn: ({ noResponse }) => {
            const model = {
                date: moment(selectedDate).format('YYYY-MM-DD'),
                dispatchGroupId,
                resendNoResponse: noResponse,
            };

            return httpClient.post(
                ApiRoutes.jobAssignment.notify(),
                model,
            );
        },
        onSuccess: (response) => {
            queryClient.invalidateQueries({ queryKey: query.queryKey });

            if (!isEmpty(response?.errorMessages)) {
                toasty.warning(
                    'Notifications completed with errors',
                    `${response.errorMessages}`,
                    { autoClose: false, closeOnClick: true },
                );
            }
        },
    });

    const handleNotificationButtonClick = useCallback(() => {
        Modal.show({
            title: 'Send Notifications',
            onSubmit: handleSendNotifications.mutateAsync,
            SaveButtonProps: {
                label: 'Send',
                savingLabel: 'Sending Notifications and Updating Jobs',
                icon: faBolt,
            },
            body: () => (
                <FormCheckbox
                    className="ml-1 mt-2"
                    id="noResponse"
                    name="noResponse"
                    labelText="Include employees with no response"
                />
            ),
        });
    }, [handleSendNotifications.mutateAsync]);

    const handleAutoAssign = useMutation({
        mutationFn: async ({ regenerate }) => {
            const model = {
                date: moment(selectedDate).format('YYYY-MM-DD'),
                dispatchGroupId,
                regenerate,
            };

            try {
                const response = await httpClient.post(
                    ApiRoutes.dispatch.autoAssign(),
                    model,
                );

                return response;
            } catch (e) {
                toasty.error('Something went wrong', e?.handledError?.message ?? e.message);
            }
        },
        onSuccess: (response) => {
            queryClient.invalidateQueries({ queryKey: query.queryKey });

            if (Array.isArray(response)) {
                toasty.success(`${pluralize(response.length, 'job')} updated successfully`, 'It may take a moment for the updated assignments to appear.');
            }
        },
    });

    const handleAutoAssignButtonClick = useCallback(() => {
        Modal.show({
            title: 'Auto-Assign',
            onSubmit: handleAutoAssign.mutateAsync,
            // With every slot already filled, only recalculating can change anything.
            initialValues: { regenerate: !hasOpenSlots },
            SaveButtonProps: {
                label: 'Auto-Assign',
                savingLabel: 'Performing magic...',
                icon: faWandMagicSparkles,
            },
            body: () => (
                <>
                    <p>
                        Auto-Assign fills the open slots for this day. It can also recalculate the day from
                        scratch, which is useful when a new job has come in since the last run.
                    </p>

                    {!hasOpenSlots && (
                        <Alert
                            color="danger"
                            className="mt-2"
                        >
                            Every slot for this day is already filled, so Auto-Assign can only recalculate.
                            The scheduled assignments you see now will be replaced, and the crew may change.
                        </Alert>
                    )}

                    <FormCheckbox
                        className="ml-1 mt-2"
                        id="regenerate"
                        name="regenerate"
                        disabled={!hasOpenSlots}
                        labelText="Recalculate the day and replace scheduled assignments"
                    />

                    <p className="text-muted small mb-0 mt-2">
                        Manual assignments are always kept, along with anyone already confirmed or awaiting
                        a response — recalculating only replaces assignments still marked scheduled.
                    </p>
                </>
            ),
        });
    }, [handleAutoAssign.mutateAsync, hasOpenSlots]);

    return (
        <CardFooter
            tag="footer"
            className="border-0 bg-light p-3"
        >
            <FlexEndRow>
                <StyledButton
                    id="auto-assign-button"
                    color="secondary"
                    startIcon={faWandMagicSparkles}
                    className="mr-2"
                    onClick={handleAutoAssignButtonClick}
                    disabled={!canAutoAssign}
                    outlined
                >
                    Auto-Assign
                </StyledButton>

                {!canAutoAssign && (
                    <UncontrolledTooltip target="auto-assign-button">
                        {isReplaceInProgress
                            ? 'Finish or cancel the in-progress replacement before auto-assigning'
                            : 'There are no open slots to fill and no auto-assigned assignments to recalculate'}
                    </UncontrolledTooltip>
                )}

                <StyledButton
                    id="sendNotification"
                    type="button"
                    color="primary"
                    className="text-nowrap"
                    startIcon={faBolt}
                    onClick={handleNotificationButtonClick}
                >
                    Send Notifications
                </StyledButton>
            </FlexEndRow>
        </CardFooter>
    );
};